import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconDownload,
  IconFileTypePdf,
  IconPlus,
  IconRecycle,
} from "@tabler/icons-react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import { ReportSectionHeader } from "../ReportSectionHeader";
import { getContractsByAddressSystem } from "../../../services/ContractService";
import { ContractListItem } from "../../../types/UserRole";
import { DeductionReleaseListItemType } from "../../../types/DeductionRelease";
import {
  downloadSignedDeductionRelease,
  getDeductionReleaseDetail,
  getDeductionReleases,
  releaseDeductionRelease,
} from "../../../services/DeductionReleaseService";
import DeductionReleasePDF from "../../pdf/DeductionReleasePDF";
import CreateDeductionReleaseModal from "./CreateDeductionReleaseModal";

interface DeductionReleaseReportProps {
  onBack: () => void;
  showHeader?: boolean;
}

function signingStatusLabel(
  item: DeductionReleaseListItemType,
  t: (key: string, fallback: string) => string,
): string {
  const base = t(
    `SIGNING_STATUS_${item.signingStatus}`,
    item.signingStatus === 99
      ? "Complete"
      : item.signingStatus === -1
        ? "Released"
        : item.signingStatus === 2
          ? "In progress"
          : item.signingStatus === 3
            ? "On hold"
            : item.signingStatus === 4
              ? "Canceled"
              : "Pending",
  );

  if (item.signingStatus === 1 && item.primarySigner) {
    return `${base} (${item.primarySigner})`;
  }
  if (item.signingStatus === 2 && item.secondarySigner) {
    return `${base} (${item.secondarySigner})`;
  }
  if (item.signingStatus === -1 && item.releaseComment) {
    return `${base}: ${item.releaseComment}`;
  }
  return base;
}

export default function DeductionReleaseReport({
  onBack,
  showHeader = true,
}: Readonly<DeductionReleaseReportProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const currentUserId = useTransientStore((state) => state.currentUserId) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 518,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  const canShow = checkAccess("CITY_OBJECT_DETUCTION_RELEASE_SHOW");
  const canModify = checkAccess("CITY_OBJECT_DETUCTION_RELEASE_MODIFY");

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );
  const [releases, setReleases] = useState<DeductionReleaseListItemType[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [releaseTarget, setReleaseTarget] =
    useState<DeductionReleaseListItemType | null>(null);
  const [releaseComment, setReleaseComment] = useState("");
  const [releasing, setReleasing] = useState(false);

  const contractId = selectedContractId ? Number(selectedContractId) : 0;

  useEffect(() => {
    if (!selectedAddressSystemId || !canShow) {
      setContracts([]);
      setSelectedContractId(null);
      return;
    }

    const load = async () => {
      setContractsLoading(true);
      setError(null);
      try {
        const items = await getContractsByAddressSystem(
          selectedAddressSystemId,
        );
        setContracts(items);
        if (items.length > 0) {
          setSelectedContractId(String(items[0].id));
        } else {
          setSelectedContractId(null);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("ERROR_LOAD_CONTRACTS", "Failed to load contracts"),
        );
      } finally {
        setContractsLoading(false);
      }
    };

    void load();
  }, [selectedAddressSystemId, canShow, t]);

  const loadReleases = useCallback(async () => {
    if (!contractId || !selectedAddressSystemId || !currentUserId) {
      setReleases([]);
      return;
    }
    setListLoading(true);
    setError(null);
    try {
      const rows = await getDeductionReleases(
        contractId,
        selectedAddressSystemId,
        currentUserId,
      );
      setReleases(rows);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : null) ??
        t("ERROR_LOAD_LIST", "Failed to load deduction releases");
      setError(message);
      setReleases([]);
    } finally {
      setListLoading(false);
    }
  }, [contractId, selectedAddressSystemId, currentUserId, t]);

  useEffect(() => {
    void loadReleases();
  }, [loadReleases]);

  const contractOptions = useMemo(
    () =>
      contracts.map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [contracts],
  );

  const handleViewPdf = async (id: number) => {
    try {
      const detail = await getDeductionReleaseDetail(
        id,
        contractId,
        selectedAddressSystemId,
        currentUserId,
      );
      const blob = await pdf(<DeductionReleasePDF detail={detail} />).toBlob();
      saveAs(blob, `pidatysten_palautus_${contractId}_${id}.pdf`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : null) ??
        t("ERROR_PDF", "Failed to generate PDF");
      setError(message);
    }
  };

  const handleDownloadSigned = async (id: number) => {
    try {
      const blob = await downloadSignedDeductionRelease(
        id,
        contractId,
        selectedAddressSystemId,
        currentUserId,
      );
      saveAs(blob, `pidatysten_palautus_signed_${contractId}_${id}.pdf`);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : null) ??
        t("ERROR_DOWNLOAD", "Failed to download signed file");
      setError(message);
    }
  };

  const handleConfirmRelease = async () => {
    if (!releaseTarget) return;
    if (!releaseComment.trim()) {
      setError(t("ERROR_RELEASE_COMMENT", "Release comment is required"));
      return;
    }
    setReleasing(true);
    setError(null);
    try {
      await releaseDeductionRelease(releaseTarget.id, {
        contractId,
        addressSystemId: selectedAddressSystemId,
        userId: currentUserId,
        comment: releaseComment.trim(),
      });
      setReleaseTarget(null);
      setReleaseComment("");
      await loadReleases();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : null) ??
        t("ERROR_RELEASE", "Failed to release deduction");
      setError(message);
    } finally {
      setReleasing(false);
    }
  };

  if (!canShow) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        {t("NO_PERMISSION", "You do not have permission to view deduction releases")}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {showHeader && (
        <ReportSectionHeader
          onBack={onBack}
          title={t("PAGE_TITLE", "Deduction release")}
          backLabel={t("BACK_TO_REPORTS", "Back to reports")}
          contextLabel={t("REPORTS_CONTEXT", "Reports")}
        />
      )}

      {!showHeader && (
        <Text fw={700} size="lg">
          {t("PAGE_TITLE", "Deduction release")}
        </Text>
      )}

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          onClose={() => setError(null)}
          withCloseButton
        >
          {error}
        </Alert>
      )}

      <Select
        label={t("SELECT_CONTRACT", "Select contract")}
        data={contractOptions}
        value={selectedContractId}
        onChange={setSelectedContractId}
        searchable
        disabled={contractsLoading}
        placeholder={t("PLACEHOLDER_CHOOSE", "Choose")}
      />

      {contractsLoading || listLoading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        contractId > 0 && (
          <Table striped withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("COL_DATE", "Date")}</Table.Th>
                <Table.Th>{t("COL_TYPE", "Type")}</Table.Th>
                <Table.Th>{t("COL_COMMENT", "Comment")}</Table.Th>
                <Table.Th>{t("COL_SA", "SA")}</Table.Th>
                <Table.Th style={{ width: 120 }}>
                  {canModify && (
                    <ActionIcon
                      variant="light"
                      color="green"
                      onClick={() => setCreateOpen(true)}
                      aria-label={t("BUTTON_ADD", "Add")}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                  )}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {releases.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center">
                      {t("EMPTY_LIST", "No deduction releases for this contract")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {releases.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    {new Date(row.created).toLocaleDateString("fi-FI")}
                  </Table.Td>
                  <Table.Td>{row.objectTypeLabel}</Table.Td>
                  <Table.Td>{row.description}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm">{signingStatusLabel(row, t)}</Text>
                      {row.hasSignedFiles === 1 && (
                        <Tooltip label={t("DOWNLOAD_SIGNED", "Download signed")}>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => void handleDownloadSigned(row.id)}
                            aria-label={t("DOWNLOAD_SIGNED", "Download signed")}
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <Tooltip label={t("VIEW_PDF", "View PDF")}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => void handleViewPdf(row.id)}
                          aria-label={t("VIEW_PDF", "View PDF")}
                        >
                          <IconFileTypePdf size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {canModify && row.canRelease && (
                        <Tooltip label={t("RELEASE", "Release")}>
                          <ActionIcon
                            variant="subtle"
                            color="orange"
                            onClick={() => {
                              setReleaseTarget(row);
                              setReleaseComment("");
                            }}
                            aria-label={t("RELEASE", "Release")}
                          >
                            <IconRecycle size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )
      )}

      <CreateDeductionReleaseModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        contractId={contractId}
        addressSystemId={selectedAddressSystemId}
        userId={currentUserId}
        getTranslation={getTranslation}
        onCreated={() => void loadReleases()}
      />

      <Modal
        opened={releaseTarget !== null}
        onClose={() => setReleaseTarget(null)}
        title={t("RELEASE_TITLE", "Release deduction")}
      >
        <Stack>
          <Textarea
            label={t("RELEASE_COMMENT", "Reason for release")}
            withAsterisk
            value={releaseComment}
            onChange={(e) => setReleaseComment(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setReleaseTarget(null)}
              disabled={releasing}
            >
              {t("BUTTON_CANCEL", "Cancel")}
            </Button>
            <Button
              color="orange"
              loading={releasing}
              onClick={() => void handleConfirmRelease()}
            >
              {t("BUTTON_RELEASE", "Release")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
